import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';
import { LogOut, AlertTriangle, X } from 'lucide-react';
import Dashboard from './Dashboard/Dashboard';
import MainLayout from './Layouts/MainLayout';
import { AuthProvider, useAuth } from './stateManagers/services/AuthContext';
import { LoadingScreen } from './components/LoadingScreen';
import LoginScreen from './Layouts/Authorisation/LoginScreen';
import { getPathForRoute } from './config/routeConfig';
import Leads from './Layouts/Leads/Leads';
import Members from './Layouts/StaffMembers/Members';
import LeadDetailView from './Layouts/Leads/LeadDetails';
import ImportLeads from './Layouts/LeadsImportExport.tsx/ImportLeads';
import AgencySetup from './Layouts/Agency/AgencySetup';
import { userProfileStore } from './stateManagers/stores/userProfileStore';
import ProfileView from './Layouts/Profile/ProfileView';

// Logout confirmation modal component
const LogoutModal: React.FC<{
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ onConfirm, onCancel }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const handleCancel = () => {
    if (confirming) return;
    setIsClosing(true);
    setTimeout(onCancel, 200);
  };

  const handleConfirm = async () => {
    setConfirming(true);
    await onConfirm();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      onClick={handleCancel}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(10,30,28,0.45)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px',
        animation: isClosing ? 'lm-maskOut 0.2s ease forwards' : 'lm-maskIn 0.2s ease',
      }}
    >
      <style>{`
        @keyframes lm-maskIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes lm-maskOut { from { opacity: 1; } to { opacity: 0; } }
        @keyframes lm-panelIn { from { opacity: 0; transform: scale(0.94) translateY(-10px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        @keyframes lm-panelOut { from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(0.94) translateY(8px); } }
        @keyframes lm-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .lm-spin { animation: lm-spin 0.8s linear infinite; }
      `}</style>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#f8f9f9',
          border: '1px solid #e8ebeb',
          borderRadius: '18px',
          padding: '5px',
          maxWidth: '380px',
          width: '100%',
          boxShadow: '0 24px 48px -12px rgba(17,85,80,0.3)',
          animation: isClosing
            ? 'lm-panelOut 0.2s cubic-bezier(0.4,0,0.2,1) forwards'
            : 'lm-panelIn 0.25s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        <div
          style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '14px',
            padding: '26px 24px 20px',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '-24px',
              right: '-24px',
              width: '110px',
              height: '110px',
              background: 'rgba(239,68,68,0.08)',
              borderRadius: '50%',
              filter: 'blur(30px)',
              pointerEvents: 'none',
            }}
          />

          <button
            onClick={handleCancel}
            disabled={confirming}
            style={{
              position: 'absolute',
              top: '14px',
              right: '14px',
              width: '26px',
              height: '26px',
              borderRadius: '7px',
              border: 'none',
              background: 'rgba(0,0,0,0.04)',
              color: '#6b7280',
              cursor: confirming ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => {
              if (!confirming) e.currentTarget.style.background = 'rgba(0,0,0,0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0,0,0,0.04)';
            }}
          >
            <X size={13} />
          </button>

          <div
            style={{
              width: '48px',
              height: '48px',
              background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
              border: '1px solid rgba(239,68,68,0.18)',
              borderRadius: '13px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <AlertTriangle size={22} color="#dc2626" strokeWidth={2} />
          </div>

          <h3
            style={{
              fontSize: '1.05rem',
              fontWeight: 700,
              color: '#111827',
              margin: '0 0 6px 0',
              position: 'relative',
              zIndex: 1,
            }}
          >
            Confirm Logout
          </h3>

          <p
            style={{
              fontSize: '0.83rem',
              color: '#6b7280',
              margin: '0 0 22px 0',
              lineHeight: 1.55,
              position: 'relative',
              zIndex: 1,
            }}
          >
            You'll need to log in again to access your dashboard.
          </p>

          <div style={{ display: 'flex', gap: '9px', position: 'relative', zIndex: 1 }}>
            <button
              onClick={handleCancel}
              disabled={confirming}
              style={{
                flex: 1,
                padding: '10px 16px',
                background: 'white',
                color: '#374151',
                border: '1px solid #e5e7eb',
                borderRadius: '9px',
                fontSize: '0.81rem',
                fontWeight: 600,
                cursor: confirming ? 'not-allowed' : 'pointer',
                opacity: confirming ? 0.5 : 1,
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                if (!confirming) e.currentTarget.style.background = '#f9fafb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
              }}
            >
              Cancel
            </button>

            <button
              onClick={handleConfirm}
              disabled={confirming}
              style={{
                flex: 1,
                padding: '10px 16px',
                background: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '9px',
                fontSize: '0.81rem',
                fontWeight: 700,
                cursor: confirming ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                boxShadow: '0 2px 8px rgba(220,38,38,0.3)',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                if (!confirming) e.currentTarget.style.background = '#b91c1c';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#dc2626';
              }}
            >
              {confirming ? (
                <>
                  <LogOut size={13} className="lm-spin" /> Logging out…
                </>
              ) : (
                <>
                  <LogOut size={13} /> Logout
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AppContent: React.FC = () => {
  const { isAuthenticated, user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [contextData, setContextData] = useState<Record<string, any>>({});

  const { profile, fetchProfile, loading: profileLoading } = userProfileStore();

  // Fetch profile once authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchProfile();
    }
  }, [isAuthenticated, fetchProfile]);

  // Redirect admin without agency to setup
  useEffect(() => {
    if (
      isAuthenticated &&
      !profileLoading &&
      profile &&
      profile.role === 'admin' &&
      !profile.agency_id &&
      location.pathname !== '/agency/setup'
    ) {
      navigate('/agency/setup', { replace: true });
    }
  }, [isAuthenticated, profile, profileLoading, location.pathname, navigate]);

  // Universal navigation handler - navigate by route ID
  const handleNavigation = (
    routeId: string,
    params?: Record<string, string>,
    data?: Record<string, any>
  ) => {
    if (data) {
      setContextData((prev) => ({ ...prev, ...data }));
    }
    const path = getPathForRoute(routeId, params);
    navigate(path);
  };

  // Navigate directly to a path (for dynamic routes)
  const handleNavigateTo = (path: string, data?: Record<string, any>) => {
    if (data) {
      setContextData((prev) => ({ ...prev, ...data }));
    }
    navigate(path);
  };

  // Clear context and go back
  const handleNavigateBack = () => {
    setContextData({});
    navigate(-1);
  };

  // Logout handlers
  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleLogoutCancel = () => setShowLogoutModal(false);
  const handleLogoutConfirm = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setShowLogoutModal(false);
    }
  };

  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <LoginScreen />;

  // Full-screen setup page (no sidebar)
  if (location.pathname === '/agency/setup') {
    return <AgencySetup />;
  }

  return (
    <>
      <MainLayout
        user={user}
        currentPath={location.pathname}
        onNavigate={handleNavigation}
        onLogout={handleLogoutClick}
        contextData={contextData}
      >
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/leads">
            <Route index element={<Leads />} />
            <Route path=":leadId/details" element={<LeadDetailView />} />
          </Route>
          <Route path="/leads/import" element={<ImportLeads />} />

          {/* Keep the route for safety / deep links; early return above handles the normal case */}
          <Route path="/agency/setup" element={<AgencySetup />} />
          <Route path="/profile" element={<ProfileView />} />

          <Route path="/staff-members">
            <Route index element={<Members />} />
          </Route>
          <Route
            path="*"
            element={
              <div style={{ padding: '40px' }}>
                <h2
                  style={{
                    fontSize: '1.8rem',
                    fontWeight: '600',
                    color: '#1a1a1a',
                    marginBottom: '16px',
                  }}
                >
                  Welcome, {user?.email}!
                </h2>
                <p style={{ color: '#666', fontSize: '1rem' }}>
                  Select a menu item from the sidebar to get started.
                </p>
              </div>
            }
          />
        </Routes>
      </MainLayout>

      {showLogoutModal && (
        <LogoutModal onConfirm={handleLogoutConfirm} onCancel={handleLogoutCancel} />
      )}
    </>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;